import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  StyleFaqRow,
  StyleFaqTextWrapper,
  StyleList,
  StyleListWrapper,
} from './style'
import { StyleText } from '@pages/front/commons/Text/style'
import { IFaq } from '@pages/front/types/type'
import { FaqMinus, FaqPlus } from '@assets/icons'
import { GeneralFAQ } from './FaqArr'

// Create motion-enhanced versions of styled components
const MotionFaqRow = motion(StyleFaqRow)
const MotionFaqTextWrapper = motion(StyleFaqTextWrapper)
const MotionListWrapper = motion(StyleListWrapper)
const MotionList = motion(StyleList)

const FaqRow = ({ faqData = GeneralFAQ }: { faqData: IFaq[] }) => {
  const [currentActiveIndex, setCurrentActiveIndex] = useState<number | null>(0)

  const toggleFaq = (index: number) => {
    setCurrentActiveIndex(currentActiveIndex === index ? null : index)
  }

  // Animation variants
  const rowVariants = {
    hover: {
      backgroundColor: 'rgba(29, 40, 106, 0.05)',
      transition: { duration: 0.2 },
    },
  }

  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: {
      opacity: 1,
      height: 'auto',
      transition: {
        opacity: { duration: 0.3 },
        height: { duration: 0.4 },
      },
    },
    exit: {
      opacity: 0,
      height: 0,
      transition: {
        opacity: { duration: 0.2 },
        height: { duration: 0.3 },
      },
    },
  }

  const iconVariants = {
    initial: { rotate: 0 },
    active: { rotate: 180 },
    hover: { scale: 1.1 },
  }

  return (
    <>
      {faqData.map((faq, i) => {
        const isActive = currentActiveIndex === i
        return (
          <MotionFaqRow
            key={i}
            isLast={i === faqData.length - 1}
            onClick={() => toggleFaq(i)}
            variants={rowVariants}
            whileHover="hover"
            initial={false}
          >
            <StyleFaqTextWrapper>
              <StyleText color="#1d286a" fontSize="17" fontSizeSm="0.8">
                {faq.question}
              </StyleText>

              <AnimatePresence>
                {isActive && (
                  <motion.div
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={contentVariants}
                    style={{ overflow: 'hidden' }}
                  >
                    <StyleText color="#414146" fontSize="13" fontSizeSm="0.6">
                      {faq.answer || ''}
                    </StyleText>
                    {faq.intro && (
                      <StyleText color="#414146" fontSize="13" fontSizeSm="0.6">
                        {faq.intro}
                      </StyleText>
                    )}
                    {faq.lists && faq.lists.length > 0 && (
                      <MotionListWrapper
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                      >
                        {faq.lists.map((item, idx) => (
                          <MotionList
                            key={idx}
                            initial={{ x: -10, opacity: 0 }}
                            animate={{ x: 0, opacity: 1 }}
                            transition={{ delay: 0.1 * idx }}
                          >
                            <StyleText
                              color="#414146"
                              fontSize="13"
                              fontSizeSm="0.6"
                            >
                              {item}
                            </StyleText>
                          </MotionList>
                        ))}
                      </MotionListWrapper>
                    )}
                    <StyleText fontSize="12">{faq.outro}</StyleText>
                  </motion.div>
                )}
              </AnimatePresence>
            </StyleFaqTextWrapper>

            <motion.div
              variants={iconVariants}
              animate={isActive ? 'active' : 'initial'}
              whileHover="hover"
            >
              <div
                dangerouslySetInnerHTML={{
                  __html: isActive ? FaqMinus : FaqPlus,
                }}
              />
            </motion.div>
          </MotionFaqRow>
        )
      })}
    </>
  )
}

export default FaqRow
